import React, { useState } from "react";
import { IGetWireStatusResponse } from "../../../services/interfaces/IQuoteUpload";
import { useSpotsContracts } from "../../../react-query/hooks/HQuoteUpload";
import ProductInfoIcon from "../../../assets/info-circle-grey.svg";

const DEFAULT_SUPPLIERS = [
  "ALAN WIRE",
  "CERROWIRE",
  "CME WIRE",
  "COLONIAL WIRE",
  "ENCORE",
  "REPUBLIC WIRE",
  "SOUTHWIRE",
];

type WireSummaryMap = Record<string, Record<string, [string[], string[]]>>;

type WirePayload = {
  Suppliers?: string[];
  spotSummary?: WireSummaryMap;
  firmFloatSummary?: WireSummaryMap;
  firmFloatSummaryNext?: WireSummaryMap;
  currentNegotiationMonth?: string;
  nextNegotiationMonth?: string;
  summary?: Record<string, Record<string, string[]>>;
};

type WireRow = {
  district: string;
  values: Record<string, { value: string; details?: string }>;
};

const EMPTY = "-";

const formatMonthYearLabel = (monthValue?: string): string | undefined => {
  if (!monthValue) {
    return undefined;
  }

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Parse yyyy-mm-dd directly to avoid timezone shifts from Date parsing
  const [year, month] = monthValue.split("-");
  const monthIndex = parseInt(month, 10) - 1;
  if (!year || monthIndex < 0 || monthIndex > 11) {
    return undefined;
  }

  return `${monthNames[monthIndex]} ${year}`;
};

const formatWeekDateUS = (dateValue?: string): string | undefined => {
  if (!dateValue) {
    return undefined;
  }

  // Handle yyyy-mm-dd directly to avoid timezone shifts from Date parsing
  const isoMatch = dateValue.trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    return `${month}/${day}/${year}`;
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return dateValue;
  }

  return parsedDate.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

const formatWeekDayMonthUS = (dateValue?: string): string | undefined => {
  if (!dateValue) {
    return undefined;
  }

  // Handle values already sent as DD/MM or DD/MM/YYYY (not parseable by Date)
  const slashMatch = dateValue.trim().match(/^(\d{1,2})\/(\d{1,2})(?:\/(\d{2,4}))?$/);
  if (slashMatch) {
    const day = slashMatch[1].padStart(2, "0");
    const month = slashMatch[2].padStart(2, "0");
    return `${month}/${day}`;
  }

  const parsedDate = new Date(dateValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return dateValue;
  }

  return parsedDate.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
  });
};

type TableEntry = { title: string; summaryKey: "spot" | "current" | "next" };

const isSummaryEmpty = (summary?: WireSummaryMap): boolean =>
  !summary || Object.keys(summary).length === 0;

const getTableEntries = (
  weekDate?: string,
  currentNegotiationMonth?: string,
  nextNegotiationMonth?: string,
  firmFloatSummary?: WireSummaryMap,
): TableEntry[] => {
  const spotTitle = weekDate
    ? `Spot Quotes for Week of ${weekDate}`
    : "Spot Quotes";

  const entries: TableEntry[] = [
    { title: spotTitle, summaryKey: "spot" },
  ];

  const currentMonthLabel = formatMonthYearLabel(currentNegotiationMonth);
  if (currentMonthLabel && !isSummaryEmpty(firmFloatSummary)) {
    entries.push({
      title: `Monthly Commitments for ${currentMonthLabel}`,
      summaryKey: "current",
    });
  }

  const nextMonthLabel = formatMonthYearLabel(nextNegotiationMonth);
  if (nextMonthLabel) {
    entries.push({
      title: `Monthly Commitments for ${nextMonthLabel}`,
      summaryKey: "next",
    });
  }

  return entries;
};

const getPayload = (statusData: IGetWireStatusResponse | undefined): WirePayload | undefined => {
  if (!statusData) {
    return undefined;
  }

  const rootData = (statusData as unknown as Record<string, any>).data;

  if (rootData) {
    const nested = rootData.data;

    if (nested) {
      return {
        Suppliers: nested.Suppliers,
        spotSummary: nested.spotSummary,
        firmFloatSummary: nested.firmFloatSummary,
        firmFloatSummaryNext: nested.firmFloatSummaryNext,
        currentNegotiationMonth: nested.current_negotiation_month,
        nextNegotiationMonth: nested.next_negotiation_month,
        summary: rootData.summary,
      };
    }

    return {
      Suppliers: rootData.Suppliers,
      summary: rootData.summary,
    };
  }

  return {
    Suppliers: (statusData as unknown as WirePayload).Suppliers,
    spotSummary: (statusData as unknown as WirePayload).spotSummary,
    firmFloatSummary: (statusData as unknown as WirePayload).firmFloatSummary,
    firmFloatSummaryNext: (statusData as unknown as WirePayload).firmFloatSummaryNext,
    currentNegotiationMonth: (statusData as unknown as WirePayload).currentNegotiationMonth,
    nextNegotiationMonth: (statusData as unknown as WirePayload).nextNegotiationMonth,
    summary: (statusData as unknown as WirePayload).summary,
  };
};

const collectSuppliersFromSummary = (summary: WireSummaryMap | undefined): string[] => {
  if (!summary) return [];
  const set = new Set<string>();
  for (const district of Object.values(summary)) {
    for (const supplier of Object.keys(district)) {
      set.add(supplier);
    }
  }
  return Array.from(set);
};

const getSuppliers = (statusData: IGetWireStatusResponse | undefined) => {
  const payload = getPayload(statusData);
  const fromPayload = payload?.Suppliers;
  const base = Array.isArray(fromPayload) && fromPayload.length
    ? fromPayload
    : DEFAULT_SUPPLIERS;

  // Merge in any suppliers that appear in the summary maps but not in the explicit list
  const fromSummaries = [
    ...collectSuppliersFromSummary(payload?.spotSummary),
    ...collectSuppliersFromSummary(payload?.firmFloatSummary),
    ...collectSuppliersFromSummary(payload?.firmFloatSummaryNext),
  ];

  const ordered = [...base];
  for (const s of fromSummaries) {
    if (!ordered.includes(s)) {
      ordered.push(s);
    }
  }

  return ordered;
};

const getCellDisplay = (cell: [string[], string[]] | undefined) => {
  if (!cell || !Array.isArray(cell)) {
    return { value: EMPTY, details: undefined as string | undefined };
  }

  const primary = Array.isArray(cell[0]) ? cell[0].filter(Boolean) : [];
  const secondary = Array.isArray(cell[1]) ? cell[1].filter(Boolean) : [];

  return {
    value: primary.length ? primary.join(", ") : EMPTY,
    details: secondary.length ? secondary.join(", ") : undefined,
  };
};

const getRows = (summary: WireSummaryMap | undefined, suppliers: string[]): WireRow[] => {
  if (!summary) {
    return [];
  }

  return Object.keys(summary).map((district) => ({
    district,
    values: suppliers.reduce<Record<string, { value: string; details?: string }>>(
      (acc, supplier) => {
        acc[supplier] = getCellDisplay(summary[district]?.[supplier]);
        return acc;
      },
      {},
    ),
  }));
};

const getLegacySuppliers = (statusData: IGetWireStatusResponse | undefined) => {
  const payload = getPayload(statusData);
  const explicitSuppliers = payload?.Suppliers;

  if (Array.isArray(explicitSuppliers) && explicitSuppliers.length) {
    return explicitSuppliers;
  }

  const summary = payload?.summary;
  if (summary && Object.keys(summary).length) {
    const firstDistrictKey = Object.keys(summary)[0];
    const firstDistrict = summary[firstDistrictKey] || {};
    const derivedSuppliers = Object.keys(firstDistrict);
    if (derivedSuppliers.length) {
      return derivedSuppliers;
    }
  }

  return DEFAULT_SUPPLIERS;
};

const getLegacyCellDisplay = (
  cell: string[] | [string[], string[]] | undefined,
) => {
  if (!Array.isArray(cell) || !cell.length) {
    return { value: EMPTY, details: undefined as string | undefined };
  }

  const isPairPayload =
    Array.isArray(cell[0]) || (cell.length > 1 && Array.isArray(cell[1]));

  if (isPairPayload) {
    const primary = Array.isArray(cell[0]) ? cell[0].filter(Boolean) : [];
    const secondary = Array.isArray(cell[1]) ? cell[1].filter(Boolean) : [];

    return {
      value: primary.length ? primary.join(", ") : EMPTY,
      details: secondary.length ? secondary.join(", ") : undefined,
    };
  }

  const flattened = cell.filter((entry): entry is string => typeof entry === "string");
  const value = flattened.length ? flattened.join(", ") : EMPTY;

  return {
    value,
    details: undefined as string | undefined,
  };
};

const renderSectionTitle = (title: string) => {
  return <span>{title}</span>;
};

const renderCellWithInfo = (cell?: { value: string; details?: string }) => {
  const value = cell?.value || EMPTY;

  return (
    <div className="flex w-full items-center justify-between gap-[0.35vw]">
      <span>{value}</span>
      {cell?.details ? (
        <img
          src={ProductInfoIcon}
          alt="details"
          title={cell.details}
          className="h-[1.5vh] w-[1.5vh] cursor-pointer"
        />
      ) : null}
    </div>
  );
};

const WireUpload = ({
  statusData,
  weekDate,
  useLegacyTable = false,
  quotationDate,
  commodity,
}: {
  statusData: IGetWireStatusResponse;
  weekDate?: string;
  useLegacyTable?: boolean;
  quotationDate?: string;
  commodity?: string;
}) => {
  void weekDate;

  const [selectedWeek, setSelectedWeek] = useState<Record<number, "current" | "previous">>({});
  const [collapsedSections, setCollapsedSections] = useState<Record<number, boolean>>({});

  const toggleSection = (sectionIndex: number) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionIndex]: !prev[sectionIndex],
    }));
  };

  const renderCollapseToggle = (sectionIndex: number, isCollapsed: boolean) => (
    <button
      type="button"
      aria-label={isCollapsed ? "Expand" : "Collapse"}
      aria-expanded={!isCollapsed}
      onClick={(event) => {
        event.stopPropagation();
        toggleSection(sectionIndex);
      }}
      className="flex h-[2.4vh] w-[2.4vh] items-center justify-center rounded border border-[#bebebe] text-[2vh] font-bold leading-none text-black hover:bg-[#f0f0f0]"
    >
      {isCollapsed ? "+" : "−"}
    </button>
  );

  const isCurrentWeekSelected = (selectedWeek[0] ?? "current") === "current";
  const isCopperWire = commodity === "Copper Wire";
  const spotsType = isCurrentWeekSelected ? "current" : "previous";
  const { spotsData, isSpotsLoading } = useSpotsContracts(
    spotsType,
    quotationDate,
    commodity,
    isCopperWire,
  );

  const payload = getPayload(statusData);
  const legacySummary = payload?.summary;
  const legacySuppliers = getLegacySuppliers(statusData);

  const spotsRecords = spotsData?.records as WireSummaryMap | undefined;
  const toggleDates = spotsData?.toggleDates as { currentDate?: string; previousDate?: string } | undefined;
  const currentWeekLabel = toggleDates?.currentDate ? `${formatWeekDayMonthUS(toggleDates.currentDate)}` : "-";
  const previousWeekLabel = toggleDates?.previousDate ? `${formatWeekDayMonthUS(toggleDates.previousDate)}` : "-";
  const spotSummaryToUse = isCopperWire && spotsRecords
    ? spotsRecords
    : payload?.spotSummary;

  // getSuppliers() only knows about statusData (Suppliers / spotSummary /
  // firmFloatSummary / firmFloatSummaryNext). For copper wire, the Spot
  // Quotes table is populated from a *separate* API (getSpotsContracts), so
  // any supplier that only appears there (e.g. COLONIAL WIRE, REPUBLIC WIRE)
  // never gets a column — its data is silently dropped and districts that
  // only quoted under that supplier show as all dashes. Merge those
  // suppliers in so every supplier present in the actual data gets a column.
  const baseSuppliers = getSuppliers(statusData);
  const spotOnlySuppliers = isCopperWire
    ? collectSuppliersFromSummary(spotSummaryToUse)
    : [];
  const suppliers = [...baseSuppliers];
  for (const supplier of spotOnlySuppliers) {
    if (!suppliers.includes(supplier)) {
      suppliers.push(supplier);
    }
  }

  const summaryMap: Record<string, WireSummaryMap | undefined> = {
    spot: spotSummaryToUse,
    current: payload?.firmFloatSummary,
    next: payload?.firmFloatSummaryNext,
  };
  const spotsWeekDate = spotsData?.week_date as string | undefined;
  const spotTableWeekDate = formatWeekDateUS(
    isCopperWire && spotsWeekDate ? spotsWeekDate : weekDate,
  );
  const tableEntries = getTableEntries(
    spotTableWeekDate,
    payload?.currentNegotiationMonth,
    payload?.nextNegotiationMonth,
    payload?.firmFloatSummary,
  );

  if (useLegacyTable) {
    const legacyRows = legacySummary ? Object.keys(legacySummary) : [];
    const isLegacyCollapsed = collapsedSections[0] ?? false;

    return (
      <div className="wireTable h-[53vh] w-full overflow-y-auto pr-[0.2vw]">
        <div className="overflow-hidden border border-[#bebebe] bg-white">
          <div className="flex items-center gap-[0.4vw] border-b border-[#bebebe] bg-white px-[0.6vw] py-[0.7vh] text-[1.8vh] font-bold text-black">
            {renderCollapseToggle(0, isLegacyCollapsed)}
            <span>Supplier Quotes</span>
          </div>
          {!isLegacyCollapsed && (
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-left text-[1.8vh] text-black">
              <thead>
                <tr className="bg-white">
                  <th className="min-w-[10rem] border border-[#bebebe] border-b-[4px] border-b-[#86bc25] px-[0.6vw] py-[0.7vh] font-bold">
                    Districts
                  </th>
                  {legacySuppliers.map((supplier) => (
                    <th
                      key={`legacy-${supplier}`}
                      className="min-w-[8.75rem] border border-[#bebebe] border-b-[4px] border-b-[#86bc25] px-[0.6vw] py-[0.7vh] font-bold"
                    >
                      {supplier}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {legacyRows.length ? (
                  legacyRows.map((district, rowIndex) => (
                    <tr
                      key={`legacy-${district}`}
                      className={rowIndex % 2 === 0 ? "bg-[#ebeee5]" : "bg-white"}
                    >
                      <td className="border border-[#bebebe] px-[0.6vw] py-[0.7vh] font-normal text-black">
                        {district}
                      </td>
                      {legacySuppliers.map((supplier) => (
                        <td
                          key={`legacy-${district}-${supplier}`}
                          className="border border-[#bebebe] px-[0.6vw] py-[0.7vh] align-top font-normal text-black"
                        >
                          {renderCellWithInfo(
                            getLegacyCellDisplay(legacySummary?.[district]?.[supplier]),
                          )}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr className="bg-white">
                    <td className="border border-[#bebebe] px-[0.6vw] py-[0.7vh] font-normal text-black">
                      -
                    </td>
                    {legacySuppliers.map((supplier) => (
                      <td
                        key={`legacy-${supplier}-empty`}
                        className="border border-[#bebebe] px-[0.6vw] py-[0.7vh] align-top font-normal text-black"
                      >
                        -
                      </td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="wireTable h-[53vh] w-full overflow-y-auto pr-[0.2vw]">
      <div className="flex flex-col gap-[1.2vh]">
        {tableEntries.map((entry, tableIndex) => {
          const title = entry.title;
          const rows = getRows(summaryMap[entry.summaryKey], suppliers);
          const accentColor = "#86bc25";
          const isCollapsed = collapsedSections[tableIndex] ?? false;

          return (
            <div
              key={`${title}-${tableIndex}`}
              className="overflow-hidden border border-[#bebebe] bg-white"
              style={{ borderLeft: `3px solid ${accentColor}` }}
            >
              <div className="flex items-center justify-between border-b border-[#bebebe] bg-white px-[0.6vw] py-[0.7vh] text-[1.8vh] font-bold text-black">
                <div className="flex items-center gap-[0.4vw]">
                  {renderCollapseToggle(tableIndex, isCollapsed)}
                  {renderSectionTitle(title)}
                </div>
                {tableIndex === 0 && (
                  <div className="flex items-center rounded-full border border-[#bebebe] bg-[#f5f5f5] p-[0.15vw] text-[1.5vh] font-medium">
                    <button
                      type="button"
                      className={`rounded-full px-[0.6vw] py-[0.3vh] transition-colors ${
                        (selectedWeek[tableIndex] ?? "current") === "current"
                          ? "bg-[#86bc25] text-white"
                          : "bg-transparent text-black"
                      }`}
                      onClick={() =>
                        setSelectedWeek((prev) => ({ ...prev, [tableIndex]: "current" }))
                      }
                    >
                      {currentWeekLabel}
                    </button>
                    <button
                      type="button"
                      className={`rounded-full px-[0.6vw] py-[0.3vh] transition-colors ${
                        selectedWeek[tableIndex] === "previous"
                          ? "bg-[#86bc25] text-white"
                          : "bg-transparent text-black"
                      }`}
                      onClick={() =>
                        setSelectedWeek((prev) => ({ ...prev, [tableIndex]: "previous" }))
                      }
                    >
                      {previousWeekLabel}
                    </button>
                  </div>
                )}
              </div>
              {!isCollapsed && (
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse text-left text-[1.8vh] text-black">
                  <thead>
                    <tr className="bg-white">
                      <th
                        className="min-w-[10rem] border border-[#bebebe] px-[0.6vw] py-[0.7vh] font-bold"
                        style={{ borderBottom: `4px solid ${accentColor}` }}
                      >
                        Districts
                      </th>
                      {suppliers.map((supplier) => (
                        <th
                          key={`${title}-${supplier}`}
                          className="min-w-[8.75rem] border border-[#bebebe] px-[0.6vw] py-[0.7vh] font-bold"
                          style={{ borderBottom: `4px solid ${accentColor}` }}
                        >
                          {supplier}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length ? (
                      rows.map((row, rowIndex) => (
                        <tr
                          key={`${title}-${row.district}`}
                          className={rowIndex % 2 === 0 ? "bg-[#ebeee5]" : "bg-white"}
                        >
                          <td className="border border-[#bebebe] px-[0.6vw] py-[0.7vh] font-normal text-black">
                            {row.district}
                          </td>
                          {suppliers.map((supplier) => (
                            <td
                              key={`${title}-${row.district}-${supplier}`}
                              className="border border-[#bebebe] px-[0.6vw] py-[0.7vh] align-top font-normal text-black"
                            >
                              {renderCellWithInfo(row.values[supplier])}
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : (
                      <tr className="bg-white">
                        <td className="border border-[#bebebe] px-[0.6vw] py-[0.7vh] font-normal text-black">
                          -
                        </td>
                        {suppliers.map((supplier) => (
                          <td
                            key={`${title}-${supplier}-empty`}
                            className="border border-[#bebebe] px-[0.6vw] py-[0.7vh] align-top font-normal text-black"
                          >
                            -
                          </td>
                        ))}
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WireUpload;